import { useState, useEffect, useCallback } from 'react';
import { isImage, fileExt, formatBytes } from '../format.js';

/**
 * The files on a task: image thumbnails with a full-screen viewer, and everything else as a
 * card that downloads.
 *
 * A thumbnail used to be a plain link to the blob URL, which meant the only way to look at a
 * screenshot properly was to leave the task — and the only way to paste one into a chat was to
 * download it first. Both are the same complaint: the image is evidence, and evidence has to be
 * usable where the task is being read. A log or an export is evidence too, but the useful thing
 * to do with it is open it in whatever reads that format, so those get a download and no viewer.
 */

/**
 * The screenshot as a PNG blob, ready for the clipboard.
 *
 * Anything that is not already a PNG goes through a canvas: the clipboard's image flavour is
 * PNG, and a JPEG handed over as `image/png` is refused rather than converted. `createImageBitmap`
 * takes the blob we already fetched, so nothing here is subject to canvas tainting.
 */
async function asPngBlob(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Fetch failed (${res.status})`);
  const blob = await res.blob();
  if (blob.type === 'image/png') return blob;

  const bitmap = await createImageBitmap(blob);
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  canvas.getContext('2d').drawImage(bitmap, 0, 0);
  bitmap.close?.();
  return new Promise((resolve, reject) => {
    canvas.toBlob(b => (b ? resolve(b) : reject(new Error('Encode failed'))), 'image/png');
  });
}

export function CopyImageButton({ url, size = '' }) {
  const [state, setState] = useState('idle');   // idle | copying | copied | failed

  const copy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!url || state === 'copying') return;
    setState('copying');
    try {
      // The PROMISE goes into the ClipboardItem, not the awaited blob. Chrome ties a clipboard
      // write to the click that started it; awaiting the fetch first spends that gesture and the
      // write is then rejected as untrusted.
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': asPngBlob(url) })]);
      setState('copied');
      setTimeout(() => setState('idle'), 2000);
    } catch {
      // Reachable two ways: the storage account has no CORS rule for GET from this origin, or
      // the browser has no image clipboard. Neither has a fallback worth building — say so
      // instead of showing "Copied" over an empty clipboard.
      setState('failed');
      setTimeout(() => setState('idle'), 3000);
    }
  };

  const label = state === 'copied' ? 'Copied'
    : state === 'failed' ? 'Could not copy'
    : state === 'copying' ? 'Copying…' : 'Copy image';

  return (
    <button type="button" className={`o-btn o-btn-ghost ${size}`.trim()}
      disabled={!url} onClick={copy}>{label}</button>
  );
}

export function Lightbox({ shot, onClose, onPrev, onNext, index, count }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, onPrev, onNext]);

  return (
    <div className="o-scrim" onClick={onClose} role="dialog" aria-modal="true"
      aria-label={shot.file_name || 'Screenshot'}>
      <div className="o-modal o-modal-wide" onClick={e => e.stopPropagation()}>
        <div className="o-modal-head">
          <h2>{shot.file_name || 'Screenshot'}</h2>
          <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {count > 1 && (
              <>
                <span className="o-note" style={{ margin: 0 }}>
                  <span className="o-num">{index + 1}</span> of <span className="o-num">{count}</span>
                </span>
                <button type="button" className="o-btn o-btn-ghost o-btn-sm"
                  onClick={onPrev} aria-label="Previous screenshot">&larr;</button>
                <button type="button" className="o-btn o-btn-ghost o-btn-sm"
                  onClick={onNext} aria-label="Next screenshot">&rarr;</button>
              </>
            )}
            <CopyImageButton url={shot.view_url} size="o-btn-sm" />
            {/* A button, not a styled <a>: .o-btn has no anchor rules, so a link wearing it
                arrives underlined and browser-blue. */}
            <button type="button" className="o-btn o-btn-ghost o-btn-sm" disabled={!shot.view_url}
              onClick={() => window.open(shot.view_url, '_blank', 'noopener')}>
              Open original
            </button>
            <button type="button" className="o-btn o-btn-primary o-btn-sm"
              onClick={onClose}>Close</button>
          </span>
        </div>
        <div className="o-modal-body" style={{ alignItems: 'center', padding: 'var(--s3)' }}>
          <img src={shot.view_url} alt={shot.file_name || 'Screenshot'}
            style={{ maxWidth: '100%', maxHeight: '78vh', objectFit: 'contain' }} />
        </div>
      </div>
    </div>
  );
}

export default function AttachmentGallery({ attachments = [] }) {
  const images = attachments.filter(isImage);
  const files = attachments.filter(a => !isImage(a));
  const [open, setOpen] = useState(null);   // null | index into `images`

  const step = useCallback((delta) => {
    setOpen(i => (i === null ? i : (i + delta + images.length) % images.length));
  }, [images.length]);

  if (!attachments.length) {
    return <p className="o-note" style={{ margin: 0 }}>None attached. Add one from Edit.</p>;
  }

  return (
    <>
      {images.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {images.map((s, i) => (
            <figure key={s.id} style={{
              margin: 0, width: 200, border: '1px solid var(--rule)',
              borderRadius: 'var(--r-md)', overflow: 'hidden', background: 'var(--surface-2)',
            }}>
              <button type="button"
                title={`${s.file_name || 'screenshot'} — click to preview`}
                onClick={() => setOpen(i)}
                style={{ display: 'block', width: '100%', border: 0, padding: 0,
                         background: 'none', cursor: s.view_url ? 'zoom-in' : 'default' }}>
                {s.view_url
                  ? <img src={s.view_url} alt={s.file_name || 'Screenshot'}
                      style={{ display: 'block', width: '100%', maxHeight: 130, objectFit: 'contain' }} />
                  : <span className="o-state" style={{ display: 'block' }}>Preview unavailable</span>}
              </button>
              <figcaption style={{ display: 'flex', gap: 4, justifyContent: 'space-between',
                padding: '3px 4px', borderTop: '1px solid var(--rule-soft)' }}>
                <button type="button" className="o-btn o-btn-ghost o-btn-sm"
                  onClick={() => setOpen(i)}>Preview</button>
                <CopyImageButton url={s.view_url} size="o-btn-sm" />
              </figcaption>
            </figure>
          ))}
        </div>
      )}

      {files.length > 0 && (
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: images.length ? 10 : 0,
        }}>
          {files.map(f => (
            // The read URL is a short-lived SAS issued with a download disposition, so this is a
            // download rather than a tab showing the raw bytes.
            <button key={f.id} type="button" className="o-file" disabled={!f.view_url}
              title={`Download ${f.file_name || 'file'}`}
              style={{ cursor: f.view_url ? 'pointer' : 'default' }}
              onClick={() => f.view_url && window.open(f.view_url, '_blank', 'noopener')}>
              <span className="o-ext">{fileExt(f.file_name)}</span>
              <span className="o-file-name">
                {f.file_name || 'File'}
                <span className="o-file-sub">
                  {f.view_url ? formatBytes(f.byte_size) || 'Download' : 'Unavailable'}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}

      {open !== null && images[open] && (
        <Lightbox
          shot={images[open]}
          index={open}
          count={images.length}
          onClose={() => setOpen(null)}
          onPrev={() => step(-1)}
          onNext={() => step(1)}
        />
      )}
    </>
  );
}
